import { useMemo } from "react";

// Treasure-hunt style SVG map of a trip: origin -> numbered stops -> X marks the spot.
export function TreasureMap({ title, origin, destination, stops }) {
  const pts = useMemo(() => {
    const raw = [];
    if (origin?.lat != null) raw.push({ kind: "origin", label: origin.label, lat: origin.lat, lon: origin.lon });
    (stops || []).forEach((s, i) => { if (s.lat != null) raw.push({ kind: "stop", n: i + 1, label: s.name, city: s.city, lat: s.lat, lon: s.lon }); });
    if (destination?.lat != null) raw.push({ kind: "dest", label: destination.label, lat: destination.lat, lon: destination.lon });
    if (raw.length < 2) return null;
    const lats = raw.map((p) => p.lat), lons = raw.map((p) => p.lon);
    const minLa = Math.min(...lats), maxLa = Math.max(...lats);
    const minLo = Math.min(...lons), maxLo = Math.max(...lons);
    const spanLa = Math.max(maxLa - minLa, 0.08), spanLo = Math.max(maxLo - minLo, 0.08);
    const W = 860, H = 460, pad = 90;
    const pts = raw.map((p, i) => ({
      ...p,
      x: pad + ((p.lon - minLo) / spanLo) * (W - pad * 2),
      y: H - pad - ((p.lat - minLa) / spanLa) * (H - pad * 2) + (i % 2 ? 10 : -10),
    }));
    // spread out markers that share (or nearly share) the same location
    for (let iter = 0; iter < 40; iter++) {
      let moved = false;
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const dx = pts[j].x - pts[i].x, dy = pts[j].y - pts[i].y;
          const d = Math.hypot(dx, dy) || 0.01;
          if (d < 72) {
            const push = (72 - d) / 2;
            const ux = (dx || 1) / d, uy = (dy || 0.6) / d;
            pts[i].x -= ux * push; pts[i].y -= uy * push;
            pts[j].x += ux * push; pts[j].y += uy * push;
            moved = true;
          }
        }
      }
      if (!moved) break;
    }
    pts.forEach((p) => {
      p.x = Math.min(W - 60, Math.max(60, p.x));
      p.y = Math.min(H - 70, Math.max(108, p.y));
    });
    return pts;
  }, [origin, destination, stops]);

  if (!pts) return null;

  const path = pts.map((p, i) => {
    if (i === 0) return `M ${p.x} ${p.y}`;
    const prev = pts[i - 1];
    const mx = (prev.x + p.x) / 2, my = (prev.y + p.y) / 2 + (i % 2 ? 26 : -26);
    return `Q ${mx} ${my} ${p.x} ${p.y}`;
  }).join(" ");

  return (
    <div className="relative rounded-3xl overflow-hidden border-2 border-amber-700/40 bg-[#14100a]" data-testid="treasure-map">
      <div className="absolute inset-0 opacity-[0.12]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #d4a24e 0%, transparent 45%), radial-gradient(circle at 80% 75%, #8a5a2b 0%, transparent 40%)" }} />
      <svg viewBox="0 0 860 460" className="w-full block" role="img" aria-label={`Trip map: ${title}`}>
        <defs>
          <filter id="rough"><feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" result="n" /><feColorMatrix in="n" type="matrix" values="0 0 0 0 0.8 0 0 0 0 0.6 0 0 0 0 0.3 0 0 0 0.04 0" /></filter>
        </defs>
        <rect width="860" height="460" fill="transparent" />
        <rect width="860" height="460" filter="url(#rough)" />
        <rect x="10" y="10" width="840" height="440" fill="none" stroke="#8a5a2b" strokeWidth="1.5" strokeDasharray="6 5" opacity="0.5" rx="18" />

        <text x="430" y="48" textAnchor="middle" fill="#e8c884" fontSize="26" fontWeight="900" fontFamily="var(--font-display, 'Archivo Black', sans-serif)" letterSpacing="1">{title}</text>
        <text x="430" y="68" textAnchor="middle" fill="#a07840" fontSize="11" letterSpacing="4">GULF COAST VIP · TRIP MAP</text>

        {/* compass */}
        <g transform="translate(62, 398)" opacity="0.85">
          <circle r="24" fill="none" stroke="#c89858" strokeWidth="1.5" />
          <path d="M0,-18 L5,0 L0,18 L-5,0 Z" fill="#c89858" />
          <text y="-28" textAnchor="middle" fill="#e8c884" fontSize="11" fontWeight="bold">N</text>
        </g>

        {/* dashed route */}
        <path d={path} fill="none" stroke="#e8b45a" strokeWidth="3" strokeDasharray="10 9" strokeLinecap="round" opacity="0.95" />

        {pts.map((p, i) => (
          <g key={i}>
            {p.kind === "origin" && (
              <g>
                <circle cx={p.x} cy={p.y} r="9" fill="#34d399" stroke="#0a0a0a" strokeWidth="2.5" />
                <path d={`M ${p.x} ${p.y - 9} v -20 l 16 5 l -16 5`} fill="#34d399" stroke="#34d399" strokeWidth="1.5" />
                <text x={p.x} y={p.y + 26} textAnchor="middle" fill="#a7f3d0" fontSize="12" fontWeight="bold">START · {p.label}</text>
              </g>
            )}
            {p.kind === "stop" && (
              <g>
                <circle cx={p.x} cy={p.y} r="14" fill="#e8b45a" stroke="#14100a" strokeWidth="3" />
                <text x={p.x} y={p.y + 4.5} textAnchor="middle" fill="#14100a" fontSize="13" fontWeight="900">{p.n}</text>
                <text x={p.x} y={p.y - 21} textAnchor="middle" fill="#f3ddb0" fontSize="11.5" fontWeight="bold">{(p.label || "").slice(0, 26)}{(p.label || "").length > 26 ? "…" : ""}</text>
                {p.city && <text x={p.x} y={p.y + 30} textAnchor="middle" fill="#a07840" fontSize="9.5">{p.city}</text>}
              </g>
            )}
            {p.kind === "dest" && (
              <g>
                <path d={`M ${p.x - 11} ${p.y - 11} L ${p.x + 11} ${p.y + 11} M ${p.x + 11} ${p.y - 11} L ${p.x - 11} ${p.y + 11}`} stroke="#f43f5e" strokeWidth="6" strokeLinecap="round" />
                <text x={p.x} y={p.y + 30} textAnchor="middle" fill="#fda4af" fontSize="12" fontWeight="bold">{p.label} · X MARKS THE SPOT</text>
              </g>
            )}
          </g>
        ))}
      </svg>
    </div>
  );
}
